import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CustomerBookingField } from 'store/customer'

import App from 'components/App'
import HelperBookingFields from 'components/Helper/HelperBookingFields/HelperBookingFields'

import styles from './DialogRequiredFields.module.scss'

export type Props = {
  open: boolean  
  unfilledFields: CustomerBookingField[]
  values: Record<string, any>
  onClose: () => void
  onComplete: (values: Record<string, any>) => void
}

const DialogRequiredFields: React.FC<Props> = ({ open, unfilledFields, values, onClose, onComplete }) => {
  const { t } = useTranslation()

  const [localUnfilledFields, setLocalUnfilledFields] = useState<CustomerBookingField[]>([])
  const [localValues, setLocalValues] = useState<Record<string, any>>({})
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setLocalUnfilledFields(unfilledFields)
      setLocalValues(values)
      setLocalErrors({})
    }
  }, [open, unfilledFields, values])

  const handleFieldChange = (fieldName: string, value: any) => {
    setLocalValues(prev => ({ ...prev, [fieldName]: value }))

    if (localErrors[fieldName]) {
      setLocalErrors(prev => ({ ...prev, [fieldName]: '' }))
    }
  }

  const handleContinue = () => {
    const requiredFields = localUnfilledFields.filter((field: CustomerBookingField) => field.required)
    const nextErrors: Record<string, string> = {}

    if (requiredFields.length > 0) {
      requiredFields.forEach((field: CustomerBookingField) => {
        const value = localValues[field.name]
        const isEmptyArray = Array.isArray(value) && value.length === 0
        const isEmptyString = typeof value === 'string' && value.trim() === ''
        const isEmptyValue = value == null || value === '' || isEmptyString || isEmptyArray

        if (isEmptyValue) {
          nextErrors[field.name] = t('This field is required')
        }
      })
    }

    setLocalErrors(nextErrors)

    if (Object.keys(nextErrors).length === 0) {
      onComplete(localValues)
      onClose()
    }
  }

  return (
    <App.Dialog
      open={open}
      onClose={onClose}
      actions={(
        <App.Flex row gap={2} padding={4}>
          <App.Button outlined flex={1} onClick={onClose}>{t('Cancel')}</App.Button>
          <App.Button flex={1} onClick={handleContinue}>{t('Continue')}</App.Button>
        </App.Flex>
      )}
    >
      <App.Flex column gap={6} className={styles.container}>
        <App.Flex column gap={3} aCenter>
          <App.Text center large bold>{t('Complete Fields')}</App.Text>
          <App.Text center muted>{t('Please fill in the fields to continue with your appointment.')}</App.Text>
        </App.Flex>

        <HelperBookingFields
          unfilledFields={localUnfilledFields}
          values={localValues}
          errors={localErrors}
          onFieldChange={handleFieldChange}
        />
      </App.Flex>
    </App.Dialog>
  )
}

export default DialogRequiredFields
